import { Check } from 'lucide-react';
import { cn } from '@/shared/lib/cn';
import { StatusDot } from '@/shared/ui';
import { MATCH_STATUS_LABELS } from '../model/labels';
import type { MatchStatus } from '../model/bracketTypes';

/** Rozmiary znacznika — dopasowane do gestosci listy, w ktorej stoi. */
const MARK_SIZE = {
  sm: { icon: 'size-3', dot: 'sm' },
  md: { icon: 'size-3.5', dot: 'md' },
} as const;

export interface MatchStatusMarkProps {
  status: MatchStatus;
  size?: keyof typeof MARK_SIZE;
  className?: string;
}

/**
 * Znacznik statusu meczu — jedna konwencja dla calej aplikacji.
 *
 * Rozstrzygniety mecz dostaje zielona fajke, mecz w trakcie zielona kropke, ktora
 * powoli pulsuje. Oba znaki sa zielone, wiec z daleka widac „ten mecz zyje albo
 * jest rozstrzygniety”, a z bliska ksztalt mowi ktore z dwojga. Pozostale statusy
 * nie maja znaku — na listach dominuja mecze zaplanowane i kropka przy kazdym
 * z nich zamieniałaby wskaznik w tlo.
 *
 * Mecz przerwany (deszcz, kontuzja) dostaje zolta kropke BEZ pulsowania. Stoi
 * w tej samej sekcji co mecze w toku, wiec musi sie od nich odrozniac: kolor
 * mowi „cos jest nie tak", a brak ruchu — „teraz sie nie gra".
 *
 * Walkower liczy sie jako rozstrzygniecie i tez dostaje fajke — na listach ma stac
 * po stronie meczow zamknietych, nie otwartych. Ze zwyklym wynikiem nie myli go
 * etykieta tekstowa („Walkower”), ktora idzie obok. Krecz zostaje bez znaku.
 *
 * Uzywac wszedzie, gdzie pokazujemy status meczu — drabinka, listy meczow, plan
 * kortow, ekran wyniku. Nie duplikowac tej logiki lokalnie.
 */
export function MatchStatusMark({ status, size = 'md', className }: MatchStatusMarkProps) {
  const scale = MARK_SIZE[size];

  if (status === 'finished' || status === 'walkover') {
    return (
      <>
        <Check
          aria-hidden="true"
          strokeWidth={3.5}
          className={cn('text-status-finished shrink-0', scale.icon, className)}
        />
        <span className="sr-only">{MATCH_STATUS_LABELS[status]}</span>
      </>
    );
  }

  if (status === 'live' || status === 'suspended') {
    return (
      <StatusDot
        status={status}
        size={scale.dot}
        pulse={status === 'live'}
        label={MATCH_STATUS_LABELS[status]}
        className={cn('shrink-0', className)}
      />
    );
  }

  return null;
}
